import React from 'react';

import IconButton from '@mui/material/IconButton';
import { Dialog, DialogActions, DialogTitle } from '@mui/material';
import DialogContent from '@mui/material/DialogContent';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Button from 'src/components/ui/Button';
import Typography from 'src/components/ui/Typography';
import Icon from 'src/components/ui/Icon';
import Menu, { MenuItem } from 'src/components/ui/Menu';

import stopPropagation from 'src/utils/stopPropagation';

import type { JobOffer } from 'src/types/__generated__/graphql';
import { useUpdateJobOfferIsHiddenMutation, useDeleteJobOfferMutation } from 'src/hooks/__generated__/queries';

import useAppRouter from 'src/hooks/useAppRouter';
import useUser from 'src/hooks/useUser';
import useAlerts from 'src/hooks/useAlerts';
import useModalOpenState from 'src/hooks/useModalOpenState';
import { routePathProfileCompanyJobs } from 'src/constants/router';

import { colours } from 'src/constants/colours';
import { styledComponent } from 'src/utils/styled';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { AnalyticsEvent, EventParamKeys } from 'src/utils/analytics';

const StyledDialogTitle = styledComponent(DialogTitle)`
    padding-top: 1.5rem;
`;

const StyledConfirmationDialog = styledComponent(Dialog)`
    max-width: 36rem;
    margin: auto;
`;

const StyledConfirmModalDialogActions = styledComponent(DialogActions)`
    padding: 0 1.5rem 1.5rem 1.5rem;
`;

interface Props {
    jobOffer: JobOffer;
    displayEditMenu?: boolean;
    showFavoriteButton?: boolean;
}

const JobOfferCardMenu: React.FC<Props> = ({ jobOffer, showFavoriteButton, displayEditMenu = false }) => {
    const { t } = useTranslation(['company']);
    const router = useAppRouter();
    const user = useUser();
    const { addAlert } = useAlerts();
    const { logEventDynamic } = useDynamicLogEvents();

    const [updateJobOfferIsHidden] = useUpdateJobOfferIsHiddenMutation();
    const [deleteJobOffer] = useDeleteJobOfferMutation();

    const { props: deleteModalProps, open: openDeleteModal } = useModalOpenState();

    const handleAddToFavorite = () => false;

    const handleEdit = (id: string) => async () => {
        await router.push(`${routePathProfileCompanyJobs}/edit-job-offer/${id}`);
    };

    const handleSetIsHidden = async () => {
        try {
            const isHidden = !jobOffer.isHidden;
            logEventDynamic(isHidden ? AnalyticsEvent.HideJobOffer : AnalyticsEvent.UnhideJobOffer);
            const variables = {
                id: jobOffer.id,
                isHidden,
            };
            await updateJobOfferIsHidden({
                variables,
                refetchQueries: ['JobOffers'],
                awaitRefetchQueries: true,
            });
            addAlert({
                type: 'success',
                message: t('company:updateJobOffer.confirmation'),
            });
        } catch (error: unknown) {
            // eslint-disable-next-line no-console
            console.log(error);
            addAlert({
                type: 'error',
                message: t('company:updateJobOffer.error'),
            });
        }
    };

    const handleDelete = (jobOfferId: string) => async () => {
        try {
            logEventDynamic(AnalyticsEvent.DeleteJobOfferConfirmation);
            const variables = {
                jobOfferId,
            };
            await deleteJobOffer({
                variables,
                refetchQueries: ['JobOffers'],
                awaitRefetchQueries: true,
            });
            addAlert({
                type: 'success',
                message: t('company:deleteJobOffer.confirmation'),
            });
        } catch (error: unknown) {
            // eslint-disable-next-line no-console
            console.log(error);
            addAlert({
                type: 'error',
                message: t('company:deleteJobOffer.error'),
            });
        }
    };

    const isOwner = jobOffer.company.id === user?.affiliatedCompany?.id;
    const maskIconName = jobOffer.isHidden ? 'Visibility' : 'VisibilityOff';
    const maskMenuItemNameKey = jobOffer.isHidden ? 'publishJobOffer' : 'hideJobOffer';

    return (
        <Box
            data-target="btn-jobOffer"
            data-target-id={`btn-jobOffer-id-${jobOffer.id}`}
            display="flex"
            flexDirection="row"
            alignItems="left"
            onClick={stopPropagation}
        >
            {isOwner && displayEditMenu && (
                <>
                    <Menu
                        id="more-menu"
                        items={[
                            <MenuItem
                                key={`editJobOffer-${jobOffer.id}`}
                                data-target="job-offer-card-button"
                                data-target-id="job-offer-card-menu-edit"
                                onClick={handleEdit(jobOffer.id)}
                            >
                                <Icon name="Edit" size="1rem" />
                                <Box ml={1}>
                                    <Typography variant="h6">{t('company:viewJobOffers.editJobOffer')}</Typography>
                                </Box>
                            </MenuItem>,
                            <MenuItem
                                key={`deleteJobOffer-${jobOffer.id}`}
                                data-target="job-offer-card-button"
                                data-target-id="job-offer-card-menu-delete"
                                onClick={() => {
                                    logEventDynamic(AnalyticsEvent.DeleteJobOffer);
                                    openDeleteModal();
                                }}
                            >
                                <Icon name="Delete" size="1rem" color={colours.primary.main} />
                                <Box ml={1}>
                                    <Typography variant="h6">{t('company:viewJobOffers.deleteJobOffer')}</Typography>
                                </Box>
                            </MenuItem>,
                            <MenuItem
                                key={`${maskMenuItemNameKey}-${jobOffer.id}`}
                                data-target="job-offer-card-button"
                                data-target-id="job-offer-card-menu-hide"
                                onClick={handleSetIsHidden}
                            >
                                <Icon name={maskIconName} size="1rem" />
                                <Box ml={1}>
                                    <Typography variant="h6">
                                        {t(`company:viewJobOffers.${maskMenuItemNameKey}`)}
                                    </Typography>
                                </Box>
                            </MenuItem>,
                        ]}
                    >
                        <IconButton
                            data-target="job-offer-card-button"
                            data-target-id="job-offer-card-menu"
                            aria-label="settings"
                            aria-controls={`jobOffer-${jobOffer.id}`}
                            aria-haspopup="true"
                        >
                            <Icon name="DotsHorizontalRounded" />
                        </IconButton>
                    </Menu>
                    <StyledConfirmationDialog {...deleteModalProps}>
                        <StyledDialogTitle>
                            <Typography
                                data-target="job-offer-card-delete-text"
                                data-target-id="job-offer-card-delete-title"
                                variant="h2"
                            >
                                {t('company:viewJobOffers.deleteJobOfferConfirmTitle')}
                            </Typography>
                        </StyledDialogTitle>
                        <DialogContent>
                            <Typography
                                data-target="job-offer-card-delete-text"
                                data-target-id="job-offer-card-delete-content"
                                color="dark"
                            >
                                {t('company:viewJobOffers.deleteJobOfferConfirmContent')}
                            </Typography>
                        </DialogContent>
                        <StyledConfirmModalDialogActions>
                            <Button
                                data-target="job-offer-card-delete-button"
                                data-target-id="job-offer-card-delete-cancel"
                                onClick={deleteModalProps.onClose}
                                variant="outlined"
                            >
                                {t('company:viewJobOffers.deleteJobOfferConfirmCancel')}
                            </Button>
                            <Button
                                data-target="job-offer-card-delete-button"
                                data-target-id="job-offer-card-delete-confirm"
                                onClick={handleDelete(jobOffer.id)}
                            >
                                {t('company:viewJobOffers.deleteJobOfferConfirmSubmit')}
                            </Button>
                        </StyledConfirmModalDialogActions>
                    </StyledConfirmationDialog>
                </>
            )}
            {showFavoriteButton && (
                <IconButton
                    aria-label="settings"
                    aria-controls={`jobOffer-${jobOffer.id}`}
                    aria-haspopup="true"
                    onClick={handleAddToFavorite}
                >
                    <Icon name="BookmarkPrimaryInactive" />
                </IconButton>
            )}
        </Box>
    );
};

export default JobOfferCardMenu;
